import { Toast } from "@base-ui/react/toast";
import { useI18n } from "../i18n/index.js";
import { XIcon } from "./icons.jsx";

/** Named by what the colour says. The tone bar is the message; the text says why. */
export type ToastTone = "neutral" | "info" | "success" | "warning" | "danger";

type ShowOptions = {
  /** @default "neutral" */
  tone?: ToastTone;
  title: string;
  description?: string | null | undefined;
  /** Milliseconds before it dismisses itself; `0` keeps it until closed. The provider's default applies otherwise. */
  timeout?: number;
};

/**
 * Renders the toast stack, bottom-right, newest at the bottom. Mounted once by ThemeProvider.
 * Each toast is a card with a striped tone bar down its left edge, lifted on a shadow because
 * it floats over content — the same bar the save bar wears.
 */
export function Toaster() {
  const { t } = useI18n();
  const { toasts } = Toast.useToastManager();
  return (
    <Toast.Portal>
      <Toast.Viewport className="dt-toast-viewport">
        {toasts.map((item) => (
          <Toast.Root key={item.id} toast={item} className="dt-toast" data-tone={item.type ?? "neutral"}>
            <span className="dt-tone-bar" aria-hidden="true" />
            <Toast.Content className="dt-toast__body">
              {item.title != null && <Toast.Title className="dt-toast__title" />}
              {item.description != null && <Toast.Description className="dt-toast__desc" />}
            </Toast.Content>
            {item.actionProps && <Toast.Action className="dt-btn dt-btn--secondary dt-btn--sm" />}
            <Toast.Close className="dt-toast__close" aria-label={t("common.dismiss")}>
              <XIcon size={14} />
            </Toast.Close>
          </Toast.Root>
        ))}
      </Toast.Viewport>
    </Toast.Portal>
  );
}

/**
 * `toast.success("Saved")` from any component. A toast reports the outcome of an action —
 * transient, self-dismissing, never load-bearing. State the page must keep showing belongs
 * on the page (a Callout), and unsaved changes belong in the SaveBar.
 */
export function useToast() {
  const manager = Toast.useToastManager();
  const show = ({ tone = "neutral", title, description, timeout }: ShowOptions) =>
    manager.add({ type: tone, title, description: description ?? undefined, timeout });
  const push = (tone: ToastTone) => (title: string, description?: string | null, opts: { timeout?: number } = {}) =>
    show({ tone, title, description, ...(opts.timeout === undefined ? {} : { timeout: opts.timeout }) });
  return {
    show,
    neutral: push("neutral"),
    info: push("info"),
    success: push("success"),
    warning: push("warning"),
    danger: push("danger"),
    close: manager.close,
    manager,
  };
}
